import { ArrowRight, ExternalLink, ShieldCheck } from "lucide-react";
import { Link } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { dgFeatureClick } from "@/lib/dataGraph";
import {
  NACA_CARD_BODY,
  NACA_CARD_HEADLINE,
  NACA_CARD_POINTS,
  NACA_DISCLOSURE,
  NACA_PATH,
  NACA_WORKSHOP_URL,
} from "@/data/nacaPartnerData";

type Props = {
  /** Where the card is rendered — recorded with the click event. */
  surface: string;
  className?: string;
};

/**
 * Reusable NACA referral card. NACA is an independent nonprofit and we are not
 * paid for the referral, so the copy stays honest about the trade-offs.
 */
const NacaReferralCard = ({ surface, className = "" }: Props) => {
  return (
    <section
      className={`rounded-2xl border border-border bg-card p-5 sm:p-6 shadow-xs ${className}`}
      aria-labelledby="naca-referral-heading"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Worth knowing about
      </p>
      <h3
        id="naca-referral-heading"
        className="mt-1 text-lg font-bold text-foreground sm:text-xl"
        style={{ fontFamily: "var(--heading-font)" }}
      >
        {NACA_CARD_HEADLINE}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{NACA_CARD_BODY}</p>

      <ul className="mt-4 flex flex-wrap gap-2">
        {NACA_CARD_POINTS.map((point) => (
          <li
            key={point}
            className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
          >
            {point}
          </li>
        ))}
      </ul>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <Button
          asChild
          size="sm"
          className="gap-1.5"
          onClick={() => dgFeatureClick("naca_referral", `${surface}:explainer`)}
        >
          <Link to={NACA_PATH}>
            See if NACA fits you <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => dgFeatureClick("naca_referral", `${surface}:outbound`)}
        >
          <a href={NACA_WORKSHOP_URL} target="_blank" rel="noopener noreferrer">
            Attend a NACA workshop <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Button>
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
        <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary/70" aria-hidden />
        <span>{NACA_DISCLOSURE}</span>
      </p>
    </section>
  );
};

export default NacaReferralCard;
